import {
	ApartmentOutlined,
	CaretDownOutlined,
	CaretUpOutlined,
	DownOutlined,
	FilterFilled,
	LoadingOutlined,
} from "@ant-design/icons";
import { Spin, Tooltip } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import {
	ControlCenterFilters,
	ControlCenterReport,
	FilterOptionsResponse,
} from "../../types/controlCenter.types";
import { formatDateDdMmmYyyy } from "../../utils/date_formatter";
import CheckboxDropdown from "../CheckboxDropdown";
import SearchButton from "../SearchButton";

/* ─── Props ─── */
interface SectionData {
	items: ControlCenterReport[];
	total: number;
	offset: number;
	hasMore: boolean;
	isLoading: boolean;
}

const EMPTY_SECTION: SectionData = {
	items: [],
	total: 0,
	offset: 0,
	hasMore: false,
	isLoading: false,
};

interface ControlCenterTableProps {
	sectionData: Record<SectionKey, SectionData>;
	sectionCounts: Record<SectionKey, number>;
	loading?: boolean;
	isRefetching?: boolean;
	onRowClick?: (report: ControlCenterReport) => void;
	onLoadMore?: (section: SectionKey) => void;
	emailToNameMap?: Record<string, string>;
	onFiltersChange?: (filters: Partial<ControlCenterFilters>) => void;
	filters?: ControlCenterFilters;
	filterOptions?: FilterOptionsResponse;
}

/* ─── Types ─── */
type SectionKey = "overdue" | "attention" | "on_track" | "completed";

interface RowData {
	id: number;
	title: string;
	tenant: string;
	type: string;
	project: string;
	submitted: string;
	status: string;
	assigned: string;
	due: string;
}

/* ─── Constants ─── */
const statusTextMap: Record<string, string> = {
	draft: "Draft",
	in_review: "Initial review",
	in_progress: "In progress",
	final_review: "Final review",
	ready_to_close: "Approved - Open",
	changes_required: "Changes requested",
	revision_required: "Changes requested",
	closed_approved: "Approved and closed",
	closed_rejected: "Rejected and closed",
};

const ReportTypeTextMap: Record<string, string> = {
	audit: "Audit",
	incident_report: "Incident Report (IR)",
	moc: "Management of Change (MOC)",
	ofi: "Opportunity for Improvement (OFI)",
	non_conformance: "Non-Conformance Report (NCR)",
	lesson_learned: "Lesson Learned",
};

const SECTION_CONFIG: Record<
	string,
	{
		darkColor: string;
		lightColor: string;
		borderLeftColor: string;
		realKey: SectionKey;
	}
> = {
	Overdue: {
		darkColor: "bg-red-900/60",
		lightColor: "bg-red-50",
		borderLeftColor: "#FF4F4F",
		realKey: "overdue",
	},
	Attention: {
		darkColor: "bg-slate-800",
		lightColor: "bg-amber-50",
		borderLeftColor: "#FA3",
		realKey: "attention",
	},
	"On track": {
		darkColor: "bg-slate-800",
		lightColor: "bg-blue-50",
		borderLeftColor: "#00BEBE",
		realKey: "on_track",
	},
	"Completed tasks": {
		darkColor: "bg-slate-800",
		lightColor: "bg-gray-50",
		borderLeftColor: "#606060",
		realKey: "completed",
	},
};

/* ─── Helpers ─── */
const resolveTaskAssignee = (
	report: ControlCenterReport,
	emailToNameMap: Record<string, string> = {}
): { name: string; email: string } | null => {
	if (!report.task_assigned_to) return null;
	const lowerEmail = report.task_assigned_to.toLowerCase();
	const nameFromMap = emailToNameMap[lowerEmail];
	return {
		name: nameFromMap || report.task_assigned_to,
		email: report.task_assigned_to,
	};
};

const getDaysAgo = (dateStr: string): string => {
	const date = new Date(dateStr);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
	if (diffDays <= 0) return "Today";
	if (diffDays === 1) return "1 day ago";
	return `${diffDays} days ago`;
};

/* ─── Component ─── */
export default function ControlCenterTable({
	sectionData,
	sectionCounts,
	isRefetching = false,
	onRowClick,
	onLoadMore,
	emailToNameMap = {},
	onFiltersChange,
	filters,
	filterOptions,
}: ControlCenterTableProps): JSX.Element {
	const isDark = localStorage.getItem("theme") === "dark";

	const [sortConfig, setSortConfig] = useState<{
		key: "submitted_on" | "task_deadline" | null;
		direction: 1 | -1;
	}>({ key: null, direction: -1 });
	const [openSections, setOpenSections] = useState<Record<string, boolean>>({
		Overdue: true,
		Attention: true,
		"On track": true,
		"Completed tasks": false,
	});

	const handleSort = (key: "submitted_on" | "task_deadline") => {
		setSortConfig((prev) => {
			if (prev.key !== key) {
				return { key, direction: 1 };
			}
			if (prev.direction === 1) {
				return { key, direction: -1 };
			}
			return { key: null, direction: -1 };
		});
	};

	useEffect(() => {
		if (onFiltersChange) {
			onFiltersChange({
				sort_key: sortConfig.key || undefined,
				sort_direction: sortConfig.key ? sortConfig.direction : undefined,
			});
		}
	}, [sortConfig.key, sortConfig.direction, onFiltersChange]);

	/* ─── Filter options ─── */
	const { reportTypeFilterOptions, statusFilterOptions, tenantFilterOptions } =
		useMemo(() => {
			// Use full dataset options from server if available
			if (filterOptions) {
				const reportTypeFilterOptions = filterOptions.report_types.map(
					(value) => ({
						label:
							ReportTypeTextMap[value as keyof typeof ReportTypeTextMap] ||
							value,
						value,
					})
				);
				const statusFilterOptions = filterOptions.combined_statuses.map(
					(value) => ({
						label: statusTextMap[value as keyof typeof statusTextMap] || value,
						value,
					})
				);
				const tenantFilterOptions =
					filterOptions.tenants?.map((value) => ({
						label: value,
						value,
					})) || [];
				return {
					reportTypeFilterOptions,
					statusFilterOptions,
					tenantFilterOptions,
				};
			}

			// Fallback: derive from loaded rows (will be incomplete for paginated data)
			const allItems = Object.values(sectionData).flatMap((s) => s.items);
			if (allItems.length === 0)
				return {
					reportTypeFilterOptions: [],
					statusFilterOptions: [],
					tenantFilterOptions: [],
				};

			const reportTypeFilterOptions = Array.from(
				new Set(allItems.map((r) => r.report_type))
			)
				.filter(Boolean)
				.map((value) => ({
					label:
						ReportTypeTextMap[value as keyof typeof ReportTypeTextMap] || value,
					value: value as string,
				}));

			const statusFilterOptions = Array.from(
				new Set(allItems.map((r) => r.combined_status))
			)
				.filter(Boolean)
				.map((value) => ({
					label: statusTextMap[value as keyof typeof statusTextMap] || value,
					value: value as string,
				}));

			const tenantFilterOptions = Array.from(
				new Set(allItems.map((r) => r.operator))
			)
				.filter(Boolean)
				.map((value) => ({
					label: value as string,
					value: value as string,
				}));

			return {
				reportTypeFilterOptions,
				statusFilterOptions,
				tenantFilterOptions,
			};
		}, [filterOptions, sectionData]);

	return (
		<div
			className={`relative mx-auto rounded-s shadow-2xl inline-block align-top ${
				isDark
					? "bg-(--layout-header-footer-bg)"
					: "bg-white border border-gray-200"
			}`}
		>
			<Spin spinning={isRefetching}>
				<table className="min-w-275">
					<thead className="bg-(--generic-table-header-bg-color)">
						<tr>
							<HeaderCell>
								<div className="flex items-center gap-2 justify-between">
									<SearchButton
										handleSearch={(value: { value: string }) => {
											if (onFiltersChange) {
												onFiltersChange({
													report_title: value.value || undefined,
												});
											}
										}}
										headerName="Task & tenant"
										columnKey="report_title_and_tenant"
										value={filters?.report_title}
									/>
									<CheckboxDropdown
										options={tenantFilterOptions}
										onChange={(selectedValues) => {
											if (onFiltersChange) {
												onFiltersChange({
													tenants:
														selectedValues.length > 0
															? (selectedValues as string[])
															: undefined,
												});
											}
										}}
										defaultValue={filters?.tenants}
										Key="tenant"
										icon={<FilterFilled />}
										hideArrow
									/>
								</div>
							</HeaderCell>
							<HeaderCell className="min-w-50">
								<CheckboxDropdown
									options={reportTypeFilterOptions}
									onChange={(selectedValues) => {
										if (onFiltersChange) {
											onFiltersChange({
												report_types:
													selectedValues.length > 0
														? (selectedValues as string[])
														: undefined,
											});
										}
									}}
									defaultValue={filters?.report_types}
									Key="report_type"
									label="Type"
									icon={<FilterFilled />}
									hideArrow
								/>
							</HeaderCell>
							<HeaderCell className="min-w-50">
								<SearchButton
									handleSearch={(value: { value: string }) => {
										if (onFiltersChange) {
											onFiltersChange({
												project_name: value.value || undefined,
											});
										}
									}}
									headerName="Project"
									columnKey="project_name"
									value={filters?.project_name}
								/>
							</HeaderCell>
							<SortableHeaderCell
								label="Submitted on"
								sortKey="submitted_on"
								sortConfig={sortConfig}
								onSort={handleSort}
								className="min-w-50"
							/>
							<HeaderCell className="min-w-50">
								<CheckboxDropdown
									options={statusFilterOptions}
									onChange={(selectedValues) => {
										if (onFiltersChange) {
											onFiltersChange({
												combined_statuses:
													selectedValues.length > 0
														? (selectedValues as string[])
														: undefined,
											});
										}
									}}
									defaultValue={filters?.combined_statuses}
									Key="combined_status"
									label="Status"
									icon={<FilterFilled />}
									hideArrow
								/>
							</HeaderCell>
							<HeaderCell className="min-w-50">
								<SearchButton
									handleSearch={(value: { value: string }) => {
										if (onFiltersChange) {
											onFiltersChange({
												assigned_to: value.value || undefined,
											});
										}
									}}
									headerName="Assigned to"
									columnKey="assigned_to"
									value={filters?.assigned_to}
								/>
							</HeaderCell>
							<SortableHeaderCell
								label="Due date"
								sortKey="task_deadline"
								sortConfig={sortConfig}
								onSort={handleSort}
								className="min-w-50"
							/>
						</tr>
					</thead>
					<tbody>
						{Object.entries(SECTION_CONFIG).map(([sectionName, config]) => {
							const sectionKey = config.realKey;
							const section = sectionData[sectionKey] ?? EMPTY_SECTION;
							const isOpen = openSections[sectionName];
							const realCount = sectionCounts[sectionKey] ?? 0;

							return (
								<React.Fragment key={sectionName}>
									<tr className={isDark ? config.darkColor : config.lightColor}>
										<td colSpan={7} className="px-0 py-0 text-sm font-semibold">
											<button
												onClick={() =>
													setOpenSections((prev) => ({
														...prev,
														[sectionName]: !prev[sectionName],
													}))
												}
												className="w-full flex items-center gap-2 px-4 py-3 text-left border-l-[8px] cursor-pointer"
												style={{
													borderLeftColor: config.borderLeftColor,
												}}
											>
												{sectionName} ({realCount})
												<DownOutlined
													className={`text-xs ml-1 transition-transform ${
														isOpen ? "" : "-rotate-90"
													}`}
												/>
											</button>
										</td>
									</tr>
									{isOpen &&
										section.items.map((report, idx) => {
											const assignee = resolveTaskAssignee(
												report,
												emailToNameMap
											);
											const assignedStr = assignee
												? `${assignee.name} (${assignee.email})`
												: "—";

											const row: RowData = {
												id: idx + 1,
												title: `${report.report_number} ${report.report_title || report.title}`,
												tenant: report.operator,
												type:
													ReportTypeTextMap[report.report_type] ||
													report.report_type,
												project: report.project_name || "—",
												submitted: report.submitted_on
													? formatDateDdMmmYyyy(report.submitted_on)
													: "—",
												status:
													statusTextMap[report.combined_status] ||
													report.combined_status,
												assigned: assignedStr,
												due: report.task_deadline
													? formatDateDdMmmYyyy(report.task_deadline)
													: "—",
											};

											return (
												<Row
													key={`${sectionName}-${report._id}-${idx}`}
													row={row}
													isRealData={true}
													realReport={report}
													onClick={() => onRowClick?.(report)}
												/>
											);
										})}
									{isOpen && section.hasMore && (
										<tr>
											<td
												colSpan={7}
												className="text-center py-3 text-(--default-btn-color) text-sm cursor-pointer hover:underline"
												onClick={() => {
													if (!section.isLoading) {
														onLoadMore?.(sectionKey);
													}
												}}
											>
												{section.isLoading ? (
													<span className="inline-flex items-center gap-2">
														<LoadingOutlined />
														Loading...
													</span>
												) : (
													"Load more..."
												)}
											</td>
										</tr>
									)}
								</React.Fragment>
							);
						})}
					</tbody>
				</table>
			</Spin>
		</div>
	);
}

/* ─── Sub-components ─── */
function HeaderCell({
	children,
	className = "",
}: {
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<th
			className={`px-4 py-3 text-left font-medium text-(--generic-table-header-text-color) tracking-wide ${className}`}
		>
			{children}
		</th>
	);
}

function SortableHeaderCell({
	label,
	sortKey,
	sortConfig,
	onSort,
	className = "",
}: {
	label: string;
	sortKey: "submitted_on" | "task_deadline";
	sortConfig: { key: string | null; direction: 1 | -1 };
	onSort: (key: "submitted_on" | "task_deadline") => void;
	className?: string;
}) {
	const isActive = sortConfig.key === sortKey;
	return (
		<th
			className={`px-4 py-3 text-left font-medium text-(--generic-table-header-text-color) tracking-wide cursor-pointer select-none hover:opacity-80 transition-opacity ${className}`}
			onClick={() => onSort(sortKey)}
		>
			<div className="flex items-center gap-1 justify-between">
				{label}
				<span className="inline-flex flex-col leading-[0.5] ml-0.5">
					<CaretUpOutlined
						className={`text-[10px] ${
							isActive && sortConfig.direction === 1
								? "text-(--default-btn-color)"
								: "text-(--theme-main-text-color) opacity-40"
						}`}
					/>
					<CaretDownOutlined
						className={`text-[10px] ${
							isActive && sortConfig.direction === -1
								? "text-(--default-btn-color)"
								: "text-(--theme-main-text-color) opacity-40"
						}`}
					/>
				</span>
			</div>
		</th>
	);
}

function Row({
	row,
	isRealData,
	realReport,
	onClick,
}: {
	row: RowData;
	isRealData: boolean;
	realReport?: ControlCenterReport;
	onClick?: () => void;
}) {
	const isDark = localStorage.getItem("theme") === "dark";
	const rowBg = isDark ? "bg-(--ribbon-bg-color)" : "bg-white";

	return (
		<tr
			className={`border-b ${isDark ? "border-gray-500" : "border-gray-400"} hover:bg-(--table-hover-bg-color) ${
				isRealData ? "cursor-pointer" : ""
			}`}
			onClick={onClick}
		>
			<td className={`px-4 py-3 min-w-72 ${rowBg}`}>
				<div className="flex flex-col gap-2">
					<div className="text-(--default-btn-color) font-medium text-sm">
						{row.title}
						{realReport?.task_name && (
							<span className="text-(--theme-main-text-color)">
								{" "}
								- {realReport.task_name}
							</span>
						)}
					</div>
					<Tooltip
						title="Tenant"
						placement="right"
						className="flex items-center gap-1.5 text-xs opacity-60 w-fit"
					>
						<ApartmentOutlined />
						<span>{row.tenant}</span>
					</Tooltip>
				</div>
			</td>
			<td
				className={`px-4 py-3 text-sm text-(--theme-main-text-color) ${rowBg}`}
			>
				{row.type}
			</td>
			<td
				className={`px-4 py-3 text-sm text-(--theme-main-text-color) ${rowBg}`}
			>
				{row.project}
			</td>
			<td
				className={`px-4 py-3 text-sm text-(--theme-main-text-color) ${rowBg}`}
			>
				{row.submitted}
			</td>
			<td className={`px-4 py-3 text-sm ${rowBg}`}>
				<span className="inline-flex items-center gap-2 text-(--theme-main-text-color)">
					{row.status}
				</span>
			</td>
			<td
				className={`px-4 py-3 text-sm text-(--theme-main-text-color) ${rowBg}`}
			>
				{row.assigned}
			</td>
			<td
				className={`px-4 py-3 text-sm text-(--theme-main-text-color) ${rowBg}`}
			>
				{realReport?.task_deadline ? (
					row.due
				) : realReport?.task_assigned_on ? (
					<span>Assigned {getDaysAgo(realReport.task_assigned_on)}</span>
				) : (
					row.due
				)}
			</td>
		</tr>
	);
}
